import React, { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import { TextInput, Button, Group, Title, Text, Divider, Box, Image, createStyles, Select, Stack } from '@mantine/core';
import { savePost, updatePost, getPostCategories } from '../../services/api.service';
import { IconCheck } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { DateTimePicker } from '@mantine/dates';
import PostImageDropzone from './PostImageDropzone';
import { RichTextEditor, Link } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import Highlight from '@tiptap/extension-highlight';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Superscript from '@tiptap/extension-superscript';
import SubScript from '@tiptap/extension-subscript';

// Custom styles
const useStyles = createStyles((theme) => ({
	imageContainer: {
		border: '1px solid #CCCCCC',
		padding: 3,
		borderRadius: theme.radius.sm,
	}
}));

const PostForm = ({ closeModal, isEdit, post }) => {

	// Hooks
	const [isLoading, toggleLoading] = useState(false);
	const [fileURL, setFileURL] = useState('');
	const [isLogoChanged, toggleIsLogoChanged] = useState(false);
	const [postCategories, setPostCategories] = useState([]);
	const { classes } = useStyles();
	const form = useForm({
		initialValues: {
			title: '',
			body: '',
			startDate: null,
			endDate: null,
			image: null,
			postCategory: {
				id: ''
			}
		},
	});
	const editor = useEditor({
		extensions: [
			StarterKit,
			Underline,
			Link,
			Superscript,
			SubScript,
			Highlight,
			TextAlign.configure({ types: ['heading', 'paragraph'] }),
		],
		content: form.values.body,
		onUpdate({ editor }) {
			form.setFieldValue('body', editor?.getHTML());
		}
	});

	// On load
	useEffect(() => {

		// Get post categories
		getPostCategories(function (response) {

			// Loop postCategories
			var postCategories = [];
			for (var i = 0; i < response.data.length; i++) {

				// Assign
				postCategories.push({
					label: response.data[i].name,
					value: response.data[i].id,
				});

			}

			// Set state
			setPostCategories(postCategories);

		}, function (error) {

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}, []);

	// Check if edit
	useEffect(() => {
		if (isEdit) {
			form.setValues({
				title: post.title,
				body: post.body,
				startDate: new Date(post.startDate),
				endDate: new Date(post.endDate),
				image: post.image,
				postCategory: {
					id: post.postCategory ? post.postCategory.id : ''
				}
			});
			// editor?.commands.setContent(JSON.parse(post.body.slice(1, -1)));
			editor?.commands.setContent(post.body);
		}
		// eslint-disable-next-line
	}, [editor]);

	// Submit form
	const onSubmit = (values) => {

		// Set loader
		toggleLoading(true);

		// Conditions
		if (!isEdit) {

			// Call API
			savePost(values, function () {

				// Show success
				notifications.show({
					title: 'Noticia creada correctamente',
					color: 'green',
					icon: <IconCheck />
				});

				// Close after 1 second
				setTimeout(() => {

					// Close modal
					closeModal();

				}, 1000);

			}, function (error) {

				// Remove preloader
				toggleLoading(false);

				// Show error
				notifications.show({
					title: 'Error',
					message: error?.message?.[0] || 'Error inesperado.',
					color: 'red'
				});

			});

		} else {

			// Call API
			updatePost(post.id, values, function () {

				// Show success
				notifications.show({
					title: 'Noticia modificada correctamente',
					color: 'green',
					icon: <IconCheck />
				});

				// Close after 1 second
				setTimeout(() => {

					// Close modal
					closeModal();

				}, 1000);

			}, function (error) {

				// Remove preloader
				toggleLoading(false);

				// Show error
				notifications.show({
					title: 'Error',
					message: error?.message?.[0] || 'Error inesperado.',
					color: 'red'
				});

			});

		}

	}

	// Process accepted file
	const onDrop = (file) => {
		form.setFieldValue('image', file[0]);
		setFileURL(URL.createObjectURL(file[0]));
		toggleIsLogoChanged(true);
	}

	// Reject file and show error
	const onReject = (file) => {
		notifications.show({
			title: 'Error',
			message: 'Este tipo de archivo no está soportado.',
			color: 'red'
		});
	}

	return (
		<>
			{/* Form */}
			<form onSubmit={form.onSubmit(onSubmit)}>
				{/* Title */}
				<Title
					order={5}
					mb="md"
				>
					{isEdit ? 'Modificar' : ''} Información de Noticia
				</Title>
				{/* Input Group */}
				<Group grow>
					{/* Title */}
					<TextInput
						required
						label="Título"
						placeholder="Título de la noticia"
						value={form.values.title}
						onChange={(event) => form.setFieldValue('title', event.currentTarget.value)}
						{...form.getInputProps('title')}
					/>
					{/* Post Category ID */}
					<Select
						searchable
						withinPortal
						required
						label="Categoría"
						placeholder="Busca una categoría"
						value={form.values.postCategory.id}
						onChange={(event) => form.setFieldValue('postCategory.id', event.currentTarget.value)}
						{...form.getInputProps('postCategory.id')}
						nothingFound="Sin resultados"
						data={postCategories}
					/>
				</Group>
				{/* Input Group */}
				<Group
					grow
					mt="md"
				>
					{/* Start Date */}
					<DateTimePicker
						required
						label="Fecha de Inicio"
						placeholder="Elige una fecha"
						valueFormat="DD-MM-YYYY HH:mm"
						value={form.values.startDate}
						onChange={(event) => form.setFieldValue('startDate', event.currentTarget.value)}
						{...form.getInputProps('startDate')}
					/>
					{/* Start Date */}
					<DateTimePicker
						required
						label="Fecha de Finalización"
						placeholder="Elige una fecha"
						valueFormat="DD-MM-YYYY HH:mm"
						value={form.values.endDate}
						minDate={form.values.startDate}
						onChange={(event) => form.setFieldValue('endDate', event.currentTarget.value)}
						{...form.getInputProps('endDate')}
					/>
				</Group>
				{/* Input Group */}
				<Group
					grow
					mt="md"
				>
					{/* Body */}
					<Stack spacing={2}>
						{/* Label */}
						<Text fz="sm">
							Contenido
						</Text>
						{/* Editor */}
						<RichTextEditor editor={editor}>
							<RichTextEditor.Toolbar sticky stickyOffset={60}>
								<RichTextEditor.ControlsGroup>
									<RichTextEditor.Bold />
									<RichTextEditor.Italic />
									<RichTextEditor.Underline />
									<RichTextEditor.Strikethrough />
									<RichTextEditor.ClearFormatting />
									<RichTextEditor.Highlight />
								</RichTextEditor.ControlsGroup>
								<RichTextEditor.ControlsGroup>
									<RichTextEditor.H1 />
									<RichTextEditor.H2 />
									<RichTextEditor.H3 />
									<RichTextEditor.H4 />
								</RichTextEditor.ControlsGroup>
								<RichTextEditor.ControlsGroup>
									<RichTextEditor.Blockquote />
									<RichTextEditor.Hr />
									<RichTextEditor.BulletList />
									<RichTextEditor.OrderedList />
									<RichTextEditor.Subscript />
									<RichTextEditor.Superscript />
								</RichTextEditor.ControlsGroup>
								<RichTextEditor.ControlsGroup>
									<RichTextEditor.Link />
									<RichTextEditor.Unlink />
								</RichTextEditor.ControlsGroup>
								<RichTextEditor.ControlsGroup>
									<RichTextEditor.AlignLeft />
									<RichTextEditor.AlignCenter />
									<RichTextEditor.AlignJustify />
									<RichTextEditor.AlignRight />
								</RichTextEditor.ControlsGroup>
							</RichTextEditor.Toolbar>
							<RichTextEditor.Content />
						</RichTextEditor>
					</Stack>
				</Group>
				<Divider
					my="md"
				/>
				{/* Subtitle */}
				<Title
					order={5}
					mb="md"
				>
					Imagen (960x720)
				</Title>
				{/* Dropzone */}
				<Group>
					{/* Zone */}
					<PostImageDropzone
						onDrop={onDrop}
						onReject={onReject}
					/>
					{/* Preview */}
					{form.values.image !== null ?
						<Box className={classes.imageContainer}>
							<Image
								withPlaceholder
								src={isEdit && !isLogoChanged ? post.image : fileURL}
								imageProps={isEdit && !isLogoChanged ? null : { onLoad: () => URL.revokeObjectURL(fileURL) }}
								height={148}
								width={148}
								radius="sm"
							/>
						</Box>
						: null}
				</Group>
				{/* Buttons */}
				<Group
					position="apart"
					grow={!isEdit}
				>
					{/* Cancel */}
					<Button
						mt="xl"
						color="red"
						onClick={closeModal}
						disabled={isLoading}
						variant="light"
						sx={{ flexGrow: isEdit ? 2 : 1 }}
					>
						Cancelar
					</Button>
					{/* Submit */}
					<Button
						mt="xl"
						type="submit"
						loading={isLoading}
						variant="filled"
						sx={{ flexGrow: isEdit ? 2 : 1 }}
					>
						{isEdit ? 'Guardar Cambios' : 'Crear Noticia'}
					</Button>
				</Group>
			</form>
		</>
	);

}

export default PostForm;
